import React from "react";
import { withStyles } from "@material-ui/core";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField, { TextFieldProps } from "@material-ui/core/TextField";
import { localeNameShort } from "@bonliva-crm/locale";
import { Classes, ThemeType } from "@theme";
import SpeechRecMic from "./SpeechRecMic";

export type TextFieldSRProps = TextFieldProps & {};

type Props = TextFieldSRProps & {
  classes: Classes<typeof styles>;
};

type State = {
  listening: boolean;
  finalTranscript?: string;
  tempTranscript?: string;
};

const globalState = {
  listening: false
};

/**
 * Material UI TextField + Speech Recognition
 */
class TextFieldSR extends React.Component<Props, State> {
  state: State = {
    listening: false,
    finalTranscript: "",
    tempTranscript: ""
  };
  private recognition: any;

  componentWillUnmount() {
    const w = window as any;
    const BrowserSpeechRecognition =
      typeof w !== "undefined" &&
      (w.SpeechRecognition ||
        w.webkitSpeechRecognition ||
        w.mozSpeechRecognition ||
        w.msSpeechRecognition ||
        w.oSpeechRecognition);
    const recognition = BrowserSpeechRecognition ? new BrowserSpeechRecognition() : null;

    if (!recognition) {
      return;
    }
    recognition.lang = localeNameShort;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = this.updateTranscript.bind(this);
    recognition.onend = () =>
      this.setState({ tempTranscript: "" }, () => this.setListening(true));
    this.recognition = recognition;
  }

  setListening = (listening: boolean) => {
    const rest: State = { listening };
    if (!listening) {
      rest.finalTranscript = "";
    }
    this.setState(rest);
    globalState.listening = listening;
  };

  updateTranscript(event: {
    results: Array<{
      [index: number]: { transcript: string; confidence: number };
      isFinal: boolean;
    }>;
    resultIndex: number;
  }) {
    let { finalTranscript } = this.state;
    let tempTranscript = "";

    for (let i = event.resultIndex; i < event.results.length; ++i) {
      const result = event.results[i];
      const transcript = result[0].transcript;
      if (result.isFinal) {
        finalTranscript = this.concatTranscripts(finalTranscript, transcript);
      } else {
        tempTranscript = this.concatTranscripts(tempTranscript, transcript);
      }
    }
    this.setState({ finalTranscript, tempTranscript });
  }

  concatTranscripts = (...transcriptParts: string[]) =>
    transcriptParts
      .map((t) => t.trim())
      .join(" ")
      .trim();

  concatFinalAndInterim = () =>
    this.concatTranscripts(this.state.finalTranscript, this.state.tempTranscript);

  startListening = () => {
    if (!this.recognition || globalState.listening) {
      return;
    }
    try {
      this.recognition.start();
      this.setListening(true);
    } catch (DOMException) {
      // Tried to start recognition after it has already started - safe to swallow this error
    }
  };

  stopListening = (e: React.ChangeEvent<any>) => {
    const transcript = this.concatFinalAndInterim();
    (e.target as any).value = `${this.props.value}${transcript}`;
    this.setListening(false);
    globalState.listening = false;
    this.recognition.stop();
    this.props.onChange(e);
  };

  render() {
    const { classes, value, ...rest } = this.props;
    const { listening } = this.state;

    return (
      <TextField
        placeholder={"En ny kommentar"}
        {...rest}
        value={value + (listening ? this.concatFinalAndInterim() : "")}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SpeechRecMic
                off={!this.recognition}
                online={listening}
                onClick={(e) => {
                  listening ? this.stopListening(e) : this.startListening();
                }}
              />
            </InputAdornment>
          ),
          classes: {
            input: classes.textarea
          }
        }}
      />
    );
  }
}

const styles = (theme: ThemeType) => ({
  speechBtn: {
    width: 32,
    height: 32
  },

  textarea: {
    overflowY: "hidden"
  }
});

export default withStyles(styles as any)(TextFieldSR);
